import type { ClientPollCreateRequest } from '@DigitaleDoerfer/digitale-doerfer-api';
import type { FC } from 'react';
import { useController, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type {
  DocumentItemPlaceholder,
  ImagePickType,
  MediaItemPlaceholder
} from '@/features/media/types/picker';
import { PostContentEditor } from '@/features/post-editor/components/PostContentEditor';
import { useGetPostTypeFeature } from '@/features/post-editor/hooks/useGetPostFeature';
import type { PostFormValues } from '@/features/post-editor/types/postForm';
import { convertClientPollToPollCreate } from '@/features/post-editor/utils/poll';
import { PostFeedType } from '@/features/posts/types/postFeed';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';

type MediaProps = {
  images: MediaItemPlaceholder[];
  isImageUploading?: boolean;
  onPickImage: (pick: ImagePickType) => void;
  onDeleteImage: (image: MediaItemPlaceholder) => void;
  onReorderImages?: (reordered: MediaItemPlaceholder[]) => void;
  documents: DocumentItemPlaceholder[];
  maxDocuments?: number;
  isDocumentUploading?: boolean;
  onPickDocument: () => Promise<void>;
  onDeleteDocument: (document: DocumentItemPlaceholder) => void;
};

type Props = MediaProps;

/**
 * Text + poll editor bound to the shared form. Media stays a prop contract
 * (side-effecting uploads live in `useMediaPlaceholders`).
 */
export const PostTextField: FC<Props> = ({ ...media }) => {
  const { t } = useTranslation(LocalizationNameSpace.POST_CREATION, {
    keyPrefix: 'screens.post-editor'
  });
  const { control } = useFormContext<PostFormValues>();

  const channelType = useWatch({ control, name: 'channelType' });
  const marketType = useWatch({ control, name: 'marketType' });

  const textField = useController({ control, name: 'text' });
  const pollField = useController({ control, name: 'poll' });

  const postTypeFeature = useGetPostTypeFeature(channelType);

  const placeholder =
    channelType === PostFeedType.MARKET
      ? t(`placeholders.${marketType}`)
      : t(`placeholders.${channelType}`);

  const handlePollChange = (next?: ClientPollCreateRequest) =>
    pollField.field.onChange(next);

  const handlePollDelete = () => pollField.field.onChange();

  return (
    <PostContentEditor
      text={textField.field.value}
      placeholder={placeholder}
      limit={postTypeFeature.maxNumberCharsPerPost ?? 360}
      onTextChange={textField.field.onChange}
      poll={convertClientPollToPollCreate(pollField.field.value)}
      onPollChange={handlePollChange}
      onPollDelete={handlePollDelete}
      maxImages={10}
      {...media}
    />
  );
};
